import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Text } from '@react-three/drei';
import WorldLabel from './WorldLabel';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import VisitorOrb from './VisitorOrb';
import AnimatedResident from './AnimatedResident';
import SafeMount from './SafeMount';
import { MANNEQUINS, resolveBody } from '../game/mannequins';
import { useHeliTour, aboardIds } from '../city/heliTour';

// Peers — renders the OTHER people in your current area (worldStore.presences, fed by
// services/worldSocket). Every visitor is an orb with their name over it (VisitorOrb:
// members warm, guests cool). Each peer's broadcast `pos` is the peer's CAMERA
// position (eye height); the orb is planted from the floor instead, since eye height
// varies per room. Positions arrive ~8 Hz — we lerp for smoothness.
//
// ⚠ NOBODY WALKS AS AN ARTIST (Khari, 2026-10-23: *"turn it off. the artists will
// live their own lives"*). A member used to appear as their artist — its name, its
// headshot, and its rigged body walking where they walked. The artists live in the
// city on their own now, so a visitor is a visitor: the server sends the member's
// own handle and no artist face or body; the pill-and-faceplate that stood in for a
// bodiless member is an orb now (Khari, 2026-10-23), and their own view stays first
// person.
//
// …UNLESS THEY HAVE A BODY (2026-11-01, Khari: "the connection would have to
// carry your body choice … yes"). A visitor who chose a mannequin (game/
// mannequins; API peerOf `body`) is drawn as it: walking while they move,
// idling when they stop, facing the way they go. A key this World does not have
// falls back to the orb.

// Their mannequin, planted on the floor they stand on: a standing peer's eye is
// 0.75 above their floor (a sited room's floor is not at 0), a seated one's is
// lower, and for them the floor is the room's.
function PeerBody({ peer, man, groupRef }) {
  const turn = useRef();
  const last = useRef(new THREE.Vector3());
  const yaw = useRef(Number(peer.heading) || 0);
  const [moving, setMoving] = useState(false);
  useFrame((_, dt) => {
    const g = groupRef.current; const t = turn.current;
    if (!g || !t) return;
    const eyeY = Number.isFinite(peer.pos?.[1]) ? Number(peer.pos[1]) : 0.75;
    t.position.y = eyeY > 0.62 ? Math.max(-0.5, Math.min(4, eyeY - 0.75)) : 0;
    const dx = g.position.x - last.current.x; const dz = g.position.z - last.current.z;
    last.current.copy(g.position);
    const sp = dt > 0 ? Math.hypot(dx, dz) / dt : 0;
    if (sp > 0.25) {
      const want = Math.atan2(dx, dz);
      let d = want - yaw.current; d = Math.atan2(Math.sin(d), Math.cos(d));
      yaw.current += d * Math.min(1, dt * 10);
    }
    t.rotation.y = yaw.current;
    const m = sp > 0.35;
    if (m !== moving) setMoving(m);
  });
  return (
    <group ref={turn}>
      <SafeMount label={`peer body ${man.key}`} retryMs={30000}>
        <React.Suspense fallback={null}>
          <AnimatedResident meshUrl={man.url} rigPose={man.pose || null} clipUrls={[]} play={moving ? 'walk' : 'idle'} height={man.height} position={[0, 0.02, 0]} />
        </React.Suspense>
      </SafeMount>
    </group>
  );
}

function Peer({ peer }) {
  const ref = useRef();
  const bodyRef = useRef();
  const nameRef = useRef();

  // FLOOR-ANCHORED: use only the peer's X/Z, plant on the ground (y=0 in every
  // room). The broadcast pos.y is eye height and varies per room.
  const target = useRef(new THREE.Vector3(peer.pos?.[0] || 0, 0, peer.pos?.[2] || 0));
  if (peer.pos) target.current.set(peer.pos[0], 0, peer.pos[2]);
  useEffect(() => { if (ref.current) ref.current.position.copy(target.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame(({ camera }) => {
    const g = ref.current; if (!g) return;
    g.position.lerp(target.current, 0.2);
    // An orb has no front; the name turns to whoever is looking.
    const camY = Math.atan2(camera.position.x - g.position.x, camera.position.z - g.position.z);
    if (bodyRef.current) bodyRef.current.rotation.y = camY;
    if (nameRef.current) nameRef.current.rotation.y = camY;
  });

  // Proximity chat bubble — shows the peer's latest line for a few seconds.
  const [bubble, setBubble] = useState(null);
  useEffect(() => {
    if (!peer.say?.text) return undefined;
    setBubble(peer.say.text);
    const t = setTimeout(() => setBubble(null), 6000);
    return () => clearTimeout(t);
  }, [peer.say?.ts]);

  const label = peer.handle || (peer.member ? 'Member' : 'Guest');
  const man = MANNEQUINS[resolveBody(peer.body)] || null;
  // AT THE HEIGHT THEY ARE (2026-10-31, Khari, across a Jahva House table: the
  // bubble was "too high to see if I am sitting across from them"). Their
  // broadcast eye height says whether they stand (≈0.75) or sit (≈0.55), so the
  // orb hangs where their head is and the name and the bubble ride just above
  // it — instead of a fixed 1.5u up that leaves the screen at a table.
  const eyeY = Number.isFinite(peer.pos?.[1]) ? Number(peer.pos[1]) : 0.75;
  const orbH = Math.max(0.42, Math.min(0.7, eyeY - 0.05));
  const topY = man ? man.height + 0.14 : orbH + 0.28;

  return (
    <group ref={ref}>
      {man ? <PeerBody peer={peer} man={man} groupRef={ref} /> : (
        <group ref={bodyRef}>
          <VisitorOrb member={!!peer.member} height={orbH} seed={peer.id} />
        </group>
      )}
      {/* Name always faces the viewer, above the head. */}
      <group ref={nameRef}>
        <Text position={[0, topY, 0]} fontSize={0.09} color="#ffe9c4" anchorX="center" anchorY="middle" outlineWidth={0.006} outlineColor="#000">
          {label}
        </Text>
      </group>
      {/* Speech bubble (proximity chat) — DOM overlay, auto-faces the camera.
          ⚠ (2026-10-31, "narrow and tall, with incorrectly sized text"): the
          box had no width of its own inside the label's zero-width anchor, so
          `break-word` wrapped it after every LETTER, and `distanceFactor` sized
          it by distance — huge across a table, tiny across a room. Now it is
          as wide as its line (max-content, wrapping only past 240px, at word
          breaks), a constant readable size, and its BOTTOM sits just above the
          name (translate −50% / −100%), so it never covers who is talking. */}
      {bubble && (
        <WorldLabel position={[0, topY + 0.07, 0]} occlude={false} zIndexRange={[20, 0]} style={{ pointerEvents: 'none' }}>
          <div style={{ transform: 'translate(-50%, -100%)' }}>
            <div style={{
              width: 'max-content', maxWidth: 240, background: 'rgba(16,10,6,0.92)', border: '1px solid rgba(255,178,94,0.5)',
              color: '#ffe9c4', font: '600 14px/1.35 system-ui, sans-serif', padding: '6px 11px', borderRadius: 12,
              textAlign: 'center', boxShadow: '0 6px 20px rgba(0,0,0,0.4)', whiteSpace: 'normal', overflowWrap: 'break-word',
            }}>
              {bubble}
            </div>
          </div>
        </WorldLabel>
      )}
    </group>
  );
}

export default function Peers() {
  const presences = useWorldStore((s) => s.presences);
  // Aboard a heli tour, a player is inside the helicopter, not standing where they boarded.
  const heliPads = useHeliTour((s) => s.pads);
  const list = useMemo(() => { const up = aboardIds({ pads: heliPads }); return (presences || []).filter((p) => Array.isArray(p.pos) && !up.has(p.id)); }, [presences, heliPads]);
  return (
    <group>
      {list.map((p) => <Peer key={p.id || p.userId} peer={p} />)}
    </group>
  );
}
